import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Cpu,
  Activity,
  AlertTriangle,
  AlertOctagon,
  ShieldCheck,
  Wrench,
  FileText,
  MessageSquare,
  TrendingUp,
  Settings,
  ChevronRight,
  ChevronLeft,
  Clock,
  Thermometer,
  Gauge,
  Droplets,
  Zap,
  BookOpen,
  ClipboardList,
  CheckCircle,
  MapPin,
  Factory,
  Tag,
  Hash,
  Calendar,
  Plus,
} from "lucide-react";
import toast from "react-hot-toast";
import { machinesAPI, sopsAPI, maintenanceAPI } from "../api";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import clsx from "clsx";
// import toast from "react-hot-toast";
import MachineConfigModal from "../components/MachineConfigModal";
import HasPermission from "../components/HasPermission";
import { PERMISSIONS } from "../constants/roles";
import { formatIstDate, formatIstDateTime } from "../utils/date";

const statusConfig = {
  operational: {
    label: "Operational",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    dot: "bg-emerald-500",
    iconBg: "bg-emerald-50 text-emerald-600 border border-emerald-100",
  },
  maintenance: {
    label: "In Maintenance",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
    iconBg: "bg-amber-50 text-amber-600 border border-amber-100",
  },
  error: {
    label: "Error",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
    iconBg: "bg-rose-50 text-rose-600 border border-rose-100",
  },
  offline: {
    label: "Offline",
    badge: "bg-slate-100 text-slate-700 border-slate-200/80",
    dot: "bg-slate-400",
    iconBg: "bg-slate-100 text-slate-500 border border-slate-200",
  },
};

const sensorIcons = {
  temperature: Thermometer,
  pressure: Gauge,
  level: Droplets,
  power: Zap,
  vibration: Activity,
};

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color = "blue",
}) => {
  const colorThemes = {
    blue: {
      bg: "from-blue-50/90 to-white border-blue-100/80 hover:border-blue-200",
      icon: "bg-blue-100/80 text-blue-600",
      badge: "bg-blue-100 text-blue-700",
    },
    emerald: {
      bg: "from-emerald-50/90 to-white border-emerald-100/80 hover:border-emerald-200",
      icon: "bg-emerald-100/80 text-emerald-600",
      badge: "bg-emerald-100 text-emerald-700",
    },
    amber: {
      bg: "from-amber-50/90 to-white border-amber-100/80 hover:border-amber-200",
      icon: "bg-amber-100/80 text-amber-600",
      badge: "bg-amber-100 text-amber-700",
    },
    rose: {
      bg: "from-rose-50/90 to-white border-rose-100/80 hover:border-rose-200",
      icon: "bg-rose-100/80 text-rose-600",
      badge: "bg-rose-100 text-rose-700",
    },
    indigo: {
      bg: "from-indigo-50/90 to-white border-indigo-100/80 hover:border-indigo-200",
      icon: "bg-indigo-100/80 text-indigo-600",
      badge: "bg-indigo-100 text-indigo-700",
    },
  };

  const theme = colorThemes[color] || colorThemes.blue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={clsx(
        "p-4 sm:p-5 bg-gradient-to-br border rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between relative overflow-hidden group",
        theme.bg
      )}
    >
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className={clsx("p-2.5 rounded-xl flex-shrink-0", theme.icon)}>
            <Icon className="w-5 h-5" />
          </div>
          {subtitle && (
            <span
              className={clsx(
                "inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full truncate",
                theme.badge
              )}
            >
              {subtitle}
            </span>
          )}
        </div>
        <p className="text-xs sm:text-sm font-medium text-slate-500 mb-1">
          {title}
        </p>
        <p className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
          {value}
        </p>
      </div>
    </motion.div>
  );
};

const SensorCard = ({ sensor, machineId, onReadingAdded }) => {
  const [value, setValue] = useState(sensor.current_value ?? "");
  const [showSimulator, setShowSimulator] = useState(false);
  const [error, setError] = useState("");
  const Icon = sensorIcons[sensor.sensor_type] || Activity;

  const currentVal =
    sensor.current_value !== null && sensor.current_value !== undefined
      ? Number(sensor.current_value)
      : null;
  const min = sensor.min_threshold ?? 0;
  const max = sensor.max_threshold ?? 100;
  const range = max - min;

  const isBelow = currentVal !== null && currentVal < min;
  const isAbove = currentVal !== null && currentVal > max;
  const isNormal = currentVal !== null && !isBelow && !isAbove;

  // Percentage for gauge progress bar
  const percent =
    range > 0 && currentVal !== null
      ? Math.max(0, Math.min(100, ((currentVal - min) / range) * 100))
      : 50;

  const readingMutation = useMutation({
    mutationFn: (overrideVal) => {
      const valToSubmit = overrideVal !== undefined ? Number(overrideVal) : Number(value);
      return machinesAPI.addSensorReading(machineId, sensor.id, { value: valToSubmit });
    },
    onSuccess: (_data, variables) => {
      setError("");
      setShowSimulator(false);
      const numVal = typeof variables === "number" ? variables : Number(value);
      const isBreachedLow = min !== undefined && numVal < min;
      const isBreachedHigh = max !== undefined && numVal > max;

      onReadingAdded?.({
        sensor,
        value: numVal,
        isBreached: isBreachedLow || isBreachedHigh,
        breachType: isBreachedLow ? "low" : isBreachedHigh ? "high" : null,
        min,
        max,
      });
    },
    onError: (mutationError) => {
      const msg = mutationError.response?.data?.error || "Unable to submit sensor reading";
      setError(msg);
      toast.error(msg);
    },
  });

  const submitReading = (event) => {
    if (event) event.preventDefault();
    const numericValue = Number(value);
    if (value === "" || !Number.isFinite(numericValue)) {
      setError("Enter a valid numeric value");
      return;
    }
    setError("");
    readingMutation.mutate(numericValue);
  };

  const adjustValue = (delta) => {
    const current = Number(value !== "" && !Number.isNaN(Number(value)) ? value : (currentVal ?? min));
    const stepDecimals = sensor.unit === "bar" || sensor.unit === "V" ? 1 : 0;
    const nextVal = Math.round((current + delta) * 10) / 10;
    setValue(stepDecimals > 0 ? nextVal.toFixed(stepDecimals) : nextVal);
  };

  // Slider range: 50 below min value and 50 above max value
  const sliderMin = min - 50;
  const sliderMax = max + 50;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={clsx(
        "bg-white rounded-2xl border p-5 transition-all duration-200 flex flex-col justify-between shadow-xs hover:shadow-md",
        isNormal ? "border-slate-200/90" : "border-rose-300 shadow-rose-50"
      )}
    >
      <div>
        {/* Header: Icon + Title + Status */}
        <div className="flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={clsx(
                "w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border",
                isNormal
                  ? "bg-slate-50 text-slate-700 border-slate-200/80"
                  : "bg-rose-100 text-rose-700 border-rose-200"
              )}
            >
              <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h4 className="text-sm font-bold text-slate-900 truncate">
                {sensor.sensor_name}
              </h4>
              <p className="text-xs text-slate-400 capitalize">
                {sensor.sensor_type || "sensor"}
              </p>
            </div>
          </div>

          <span
            className={clsx(
              "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border shrink-0",
              isNormal
                ? "bg-emerald-50 text-emerald-700 border-emerald-200/80"
                : "bg-rose-50 text-rose-700 border-rose-200/80"
            )}
          >
            <span
              className={clsx(
                "w-1.5 h-1.5 rounded-full",
                isNormal ? "bg-emerald-500" : "bg-rose-500 animate-pulse"
              )}
            />
            {isNormal ? "Normal" : isBelow ? "Low" : "High"}
          </span>
        </div>

        {/* Primary Value Display */}
        <div className="mt-5 flex items-baseline justify-between">
          <div className="flex items-baseline gap-1.5">
            <span
              className={clsx(
                "text-3xl font-extrabold font-mono tracking-tight",
                isNormal ? "text-slate-900" : "text-rose-600"
              )}
            >
              {currentVal !== null ? currentVal.toFixed(1) : "--"}
            </span>
            <span className="text-sm font-semibold text-slate-400">
              {sensor.unit}
            </span>
          </div>

          <span className="text-xs text-slate-400 font-medium">
            Limit: {min} – {max} {sensor.unit}
          </span>
        </div>

        {/* Clean Progress Track */}
        <div className="mt-3">
          <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={clsx(
                "h-full rounded-full transition-all duration-300",
                isNormal ? "bg-blue-600" : "bg-rose-500"
              )}
              style={{ width: `${Math.min(100, Math.max(4, percent))}%` }}
            />
          </div>
        </div>
      </div>

      {/* Bottom Section: Right-aligned Simulate Toggle & Interactive Slider Bar */}
      <div className="mt-4 pt-3 border-t border-slate-100">
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={() => {
              setShowSimulator((prev) => !prev);
              if (!showSimulator && (value === "" || value === null)) {
                setValue(currentVal ?? min);
              }
            }}
            className={clsx(
              "text-xs font-medium inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-colors",
              showSimulator
                ? "text-blue-700 bg-blue-50 hover:bg-blue-100"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
            )}
          >
            <Settings className="w-3.5 h-3.5" />
            <span>{showSimulator ? "Hide Simulation" : "Simulate reading"}</span>
          </button>
        </div>

        {/* Expanded Simulator Panel with Number Field and Step Buttons */}
        {showSimulator && (
          <form onSubmit={submitReading} className="mt-3 pt-3 border-t border-slate-100 space-y-3">
            {/* Number Input Field */}
            <div className="relative">
              <input
                type="number"
                step="any"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter value"
                autoFocus
                className="w-full h-9 pl-3 pr-14 text-sm font-mono font-medium text-slate-900 bg-slate-50 hover:bg-slate-100/70 focus:bg-white border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                aria-label={`Simulation value for ${sensor.sensor_name}`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 select-none">
                {sensor.unit}
              </span>
            </div>

            {/* Quick Step Buttons & Action Buttons */}
            <div className="flex items-center justify-between gap-1.5 flex-wrap">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => adjustValue(-5)}
                  className="px-2 py-1 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 rounded-lg text-xs font-mono font-medium transition-colors"
                  title="Decrease by 5"
                >
                  -5
                </button>
                <button
                  type="button"
                  onClick={() => adjustValue(-1)}
                  className="px-2 py-1 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 rounded-lg text-xs font-mono font-medium transition-colors"
                  title="Decrease by 1"
                >
                  -1
                </button>
                <button
                  type="button"
                  onClick={() => adjustValue(1)}
                  className="px-2 py-1 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 rounded-lg text-xs font-mono font-medium transition-colors"
                  title="Increase by 1"
                >
                  +1
                </button>
                <button
                  type="button"
                  onClick={() => adjustValue(5)}
                  className="px-2 py-1 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 rounded-lg text-xs font-mono font-medium transition-colors"
                  title="Increase by 5"
                >
                  +5
                </button>
              </div>

              {/* Cancel & Send Buttons */}
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowSimulator(false);
                    setError("");
                    setValue(currentVal ?? min);
                  }}
                  className="px-2.5 py-1 text-xs font-medium text-slate-500 hover:text-slate-800 bg-white hover:bg-slate-100 border border-slate-200/90 rounded-lg transition-colors h-7"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={readingMutation.isPending}
                  className="btn btn-primary text-xs py-1 px-3 h-7 rounded-lg shadow-xs font-medium"
                >
                  {readingMutation.isPending ? "Sending..." : "Send"}
                </button>
              </div>
            </div>

            {error && <p className="text-[11px] text-rose-600 font-medium">{error}</p>}
          </form>
        )}
      </div>
    </motion.div>
  );
};

const contentVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease: "easeOut" },
  },
  exit: { opacity: 0, y: -6, transition: { duration: 0.15 } },
};

const MachineDetailSkeleton = () => (
  <div className="space-y-5 lg:space-y-6 animate-pulse">
    {/* Header Skeleton */}
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-200 shrink-0" />
        <div className="space-y-2">
          <div className="h-6 w-48 bg-slate-200 rounded" />
          <div className="h-4 w-32 bg-slate-100 rounded" />
        </div>
      </div>
      <div className="flex items-center gap-2.5">
        <div className="h-9 w-32 bg-slate-200 rounded-xl" />
        <div className="h-9 w-28 bg-slate-200 rounded-xl" />
      </div>
    </div>

    {/* Stat Cards Skeleton */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
      {[...Array(4)].map((_, i) => (
        <div
          key={i}
          className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3"
        >
          <div className="flex justify-between">
            <div className="w-10 h-10 rounded-xl bg-slate-200" />
            <div className="w-16 h-5 rounded-full bg-slate-100" />
          </div>
          <div className="h-4 w-24 bg-slate-100 rounded" />
          <div className="h-7 w-20 bg-slate-200 rounded" />
        </div>
      ))}
    </div>

    {/* Tabs Skeleton */}
    <div className="h-10 w-72 sm:w-96 rounded-full bg-slate-200/60" />

    {/* Content Skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
      <div className="bg-white border border-slate-200/80 rounded-2xl p-6 h-80" />
      <div className="lg:col-span-2 bg-white border border-slate-200/80 rounded-2xl p-6 h-80" />
    </div>
  </div>
);

const getFullTitle = (log) => {
  if (!log) return "";
  let title = typeof log === "string" ? log : log.title || "";
  if (!title) return "";

  // If title ends with '...', recover full title from description
  if (title.endsWith("...")) {
    const cleanTitle = title.slice(0, -3).trim();
    if (typeof log === "object" && log.description) {
      const desc = log.description.trim();
      const prefixMatch = cleanTitle.match(/^((?:Resolve Error|Issue Resolution|Maintenance|Fix|Ticket)\s*:\s*)(.*)$/i);
      if (prefixMatch) {
        const prefix = prefixMatch[1];
        const partial = prefixMatch[2].trim();
        if (desc.toLowerCase().startsWith(partial.toLowerCase())) {
          return `${prefix}${desc}`;
        }
        const reportedMatch = desc.match(/Reported Issue:\s*([^\n\r]+)/i);
        if (reportedMatch && reportedMatch[1].trim()) {
          const reportedText = reportedMatch[1].trim();
          if (reportedText.toLowerCase().startsWith(partial.toLowerCase())) {
            return `${prefix}${reportedText}`;
          }
        }
        if (!desc.includes("\n") && !desc.includes("Auto-generated")) {
          return `${prefix}${desc}`;
        }
      }
      if (desc.toLowerCase().startsWith(cleanTitle.toLowerCase())) {
        return desc;
      }
    }
    return cleanTitle;
  }
  return title;
};

const parseLogDetails = (log) => {
  let mainDescription = "";
  let reportedIssue = "";
  let autoGeneratedErrorLogId = null;

  if (log?.description) {
    let text = log.description.trim();

    // 1. Extract "Auto-generated ticket for Error Log: <id>"
    const autoGenMatch = text.match(/Auto-generated ticket for Error Log:\s*([a-fA-F0-9_-]+)/i);
    if (autoGenMatch) {
      autoGeneratedErrorLogId = autoGenMatch[1];
      text = text.replace(/Auto-generated ticket for Error Log:\s*[a-fA-F0-9_-]*/i, "").trim();
    }

    // 2. Extract "Reported Issue: <text>"
    const reportedMatch = text.match(/Reported Issue:\s*([\s\S]+?)(?=(?:\n\s*Auto-generated|\n\s*Notes:|$))/i);
    if (reportedMatch) {
      reportedIssue = reportedMatch[1].trim();
      text = text.replace(/Reported Issue:\s*[\s\S]+?(?=(?:\n\s*Auto-generated|\n\s*Notes:|$))/i, "").trim();
    }

    // Remaining non-boilerplate narrative text
    if (text) {
      mainDescription = text;
    }
  }

  // Fallback: If title is "Resolve Error: <issue>" and description matches or is the issue
  if (!reportedIssue && log?.title?.toLowerCase().startsWith("resolve error:") && log?.description) {
    const errorFromTitle = log.title.replace(/^resolve error:\s*/i, "").trim();
    if (log.description.trim().toLowerCase() === errorFromTitle.toLowerCase()) {
      reportedIssue = log.description.trim();
      mainDescription = "";
    }
  }

  // Fallback for autoGeneratedErrorLogId from bug_id
  if (
    !autoGeneratedErrorLogId &&
    log?.bug_id &&
    (log.is_auto_generated ||
      log.title?.toLowerCase().includes("issue resolution") ||
      log.title?.toLowerCase().includes("resolve error") ||
      log.title?.toLowerCase().includes("resolve bug"))
  ) {
    autoGeneratedErrorLogId = log.bug_id;
  }

  return {
    mainDescription,
    reportedIssue,
    autoGeneratedErrorLogId,
  };
};

const getCleanDisplayTitle = (title) => {
  if (!title) return "";
  const cleaned = title
    .replace(/^(?:Manual Fix|Resolve Error|Resolve Bug|Issue Resolution|Direct SOP|Maintenance)\s*:\s*/i, "")
    .trim();
  return cleaned || title;
};

const getCategoryConfig = (title, log) => {
  const t = (title || log?.title || "").toLowerCase();
  const maintenanceType = (log?.maintenance_type || "").toLowerCase();
  const isAuto = log?.is_auto_generated;

  // 1. Manual Fix (Technician manual resolution / repair)
  if (t.startsWith("manual fix") || (!isAuto && log?.notes && !log?.bug_id && t.includes("fix"))) {
    return {
      key: "manual_fix",
      label: "Manual Fix",
      icon: Wrench,
      borderClass: "border-l-indigo-500",
      badgeClass: "bg-indigo-50 text-indigo-700 border-indigo-200/90",
      iconBoxClass: "bg-indigo-50 text-indigo-600 border-indigo-100",
      dotClass: "bg-indigo-500",
      filterColor: "indigo",
    };
  }

  // 2. Resolve Error / Resolve Bug (Corrective fix responding to error/bug)
  if (
    t.startsWith("resolve error") ||
    t.startsWith("resolve bug") ||
    t.includes("resolve error") ||
    t.includes("resolve bug")
  ) {
    return {
      key: "resolve_error",
      label: "Resolve Error",
      icon: AlertOctagon,
      borderClass: "border-l-rose-500",
      badgeClass: "bg-rose-50 text-rose-700 border-rose-200/90",
      iconBoxClass: "bg-rose-50 text-rose-600 border-rose-100",
      dotClass: "bg-rose-500",
      filterColor: "rose",
    };
  }

  // 3. Issue Resolution (Auto-generated error ticket or system issue resolution)
  if (t.startsWith("issue resolution") || t.includes("issue resolution") || (isAuto && log?.bug_id)) {
    return {
      key: "issue_resolution",
      label: "Issue Resolution",
      icon: Cpu,
      borderClass: "border-l-amber-500",
      badgeClass: "bg-amber-50 text-amber-800 border-amber-200/90",
      iconBoxClass: "bg-amber-50 text-amber-600 border-amber-100",
      dotClass: "bg-amber-500",
      filterColor: "amber",
    };
  }

  // 4. Preventive Inspection / Routine Check
  if (
    t.includes("inspection") ||
    t.includes("routine") ||
    t.includes("weekly") ||
    t.includes("oil change") ||
    maintenanceType === "preventive"
  ) {
    return {
      key: "preventive_inspection",
      label: "Inspection",
      icon: ShieldCheck,
      borderClass: "border-l-emerald-500",
      badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200/90",
      iconBoxClass: "bg-emerald-50 text-emerald-600 border-emerald-100",
      dotClass: "bg-emerald-500",
      filterColor: "emerald",
    };
  }

  // 5. Standard Procedure / SOP
  if (t.startsWith("direct sop") || t.includes("sop") || log?.sop_id) {
    return {
      key: "sop_execution",
      label: "Standard Procedure",
      icon: BookOpen,
      borderClass: "border-l-blue-500",
      badgeClass: "bg-blue-50 text-blue-700 border-blue-200/90",
      iconBoxClass: "bg-blue-50 text-blue-600 border-blue-100",
      dotClass: "bg-blue-500",
      filterColor: "blue",
    };
  }

  // Fallback: General Maintenance
  return {
    key: "general_maintenance",
    label: "General Maintenance",
    icon: Wrench,
    borderClass: "border-l-slate-400",
    badgeClass: "bg-slate-100 text-slate-700 border-slate-200",
    iconBoxClass: "bg-slate-50 text-slate-600 border-slate-200",
    dotClass: "bg-slate-400",
    filterColor: "slate",
  };
};

const Pagination = ({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  label = "items",
}) => {
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  if (totalItems <= pageSize) return null;

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200/80">
      <p className="text-xs text-slate-500 font-medium">
        Showing{" "}
        <span className="font-semibold text-slate-700">{startItem}</span> to{" "}
        <span className="font-semibold text-slate-700">{endItem}</span> of{" "}
        <span className="font-semibold text-slate-700">{totalItems}</span>{" "}
        {label}
      </p>

      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
          className={clsx(
            "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
            currentPage <= 1
              ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
              : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
          )}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Prev</span>
        </button>

        <div className="flex items-center gap-1">
          {getPageNumbers().map((pageItem, idx) =>
            pageItem === "..." ? (
              <span
                key={`ellipsis-${idx}`}
                className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
              >
                ...
              </span>
            ) : (
              <button
                key={pageItem}
                type="button"
                onClick={() => onPageChange(pageItem)}
                className={clsx(
                  "w-8 h-8 rounded-xl text-xs font-semibold transition-all",
                  currentPage === pageItem
                    ? "bg-blue-600 text-white shadow-sm shadow-blue-500/30 font-bold"
                    : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 shadow-xs"
                )}
              >
                {pageItem}
              </button>
            )
          )}
        </div>

        <button
          type="button"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
          className={clsx(
            "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
            currentPage >= totalPages
              ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
              : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
          )}
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

const MachineDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("overview");
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [createdMaintenanceAlertIds, setCreatedMaintenanceAlertIds] = useState(new Set());
  const [readingAlertModal, setReadingAlertModal] = useState(null);
  const queryClient = useQueryClient();

  // Pagination states for all listings
  const [sensorsPage, setSensorsPage] = useState(1);
  const [maintenancePage, setMaintenancePage] = useState(1);
  const [alertsPage, setAlertsPage] = useState(1);
  const [sopsPage, setSopsPage] = useState(1);

  const SENSORS_PER_PAGE = 6;
  const MAINTENANCE_PER_PAGE = 5;
  const ALERTS_PER_PAGE = 5;
  const SOPS_PER_PAGE = 5;

  const handleReadingAdded = (result) => {
    queryClient.invalidateQueries({ queryKey: ["machine", id] });
    queryClient.invalidateQueries({ queryKey: ["machine-health", id] });
    queryClient.invalidateQueries({ queryKey: ["machine-alerts", id] });

    if (result?.isBreached) {
      setReadingAlertModal({
        sensor: result.sensor,
        value: result.value,
        breachType: result.breachType,
        min: result.min,
        max: result.max,
      });
    } else if (result) {
      toast.success(
        `${result.sensor?.sensor_name || "Telemetry"} updated to ${result.value} ${result.sensor?.unit || ""}`
      );
    }
  };

  const createMaintenanceMutation = useMutation({
    mutationFn: (alert) => maintenanceAPI.create({
      machineId: id,
      title: `Investigate alert: ${alert.title}`,
      description: `${alert.message}. Simulated actual value: ${alert.actual_value}; threshold: ${alert.threshold_value}.`,
      maintenanceType: "corrective",
      scheduledDate: new Date().toISOString(),
      bugId: alert.id,
    }),
    onSuccess: (_response, alert) => {
      setCreatedMaintenanceAlertIds((currentIds) => new Set(currentIds).add(alert.id));
      toast.success("Maintenance scheduled for this alert");
      queryClient.invalidateQueries({ queryKey: ["machine", id] });
    },
    onError: (mutationError) => {
      toast.error(mutationError.response?.data?.error || "Unable to create maintenance");
    },
  });

  const { data: machine, isLoading } = useQuery({
    queryKey: ["machine", id],
    queryFn: () => machinesAPI.getById(id).then((res) => res.data.data),
  });

  const { data: alertsData, isLoading: alertsLoading } = useQuery({
    queryKey: ["machine-alerts", id, alertsPage],
    queryFn: () => machinesAPI.getAlerts(id, { page: alertsPage, limit: ALERTS_PER_PAGE }).then((res) => res.data),
    enabled: Boolean(id),
  });

  const { data: healthData } = useQuery({
    queryKey: ["machine-health", id],
    queryFn: () => machinesAPI.getHealth(id).then((res) => res.data.data),
    refetchInterval: 30000,
  });

  const { data: sopsData } = useQuery({
    queryKey: ["machine-sops", id],
    queryFn: () => sopsAPI.getAll({ machineId: id }).then((res) => res.data),
  });
  const machineSops = sopsData?.data || [];

  // Mock Trend Data for Charts
  const sensorHistory = [
    { time: "00:00", temp: 45, pressure: 5.2 },
    { time: "04:00", temp: 48, pressure: 5.4 },
    { time: "08:00", temp: 52, pressure: 5.6 },
    { time: "12:00", temp: 55, pressure: 5.8 },
    { time: "16:00", temp: 51, pressure: 5.5 },
    { time: "20:00", temp: 47, pressure: 5.3 },
    { time: "24:00", temp: 46, pressure: 5.2 },
  ];

  if (isLoading) {
    return <MachineDetailSkeleton />;
  }

  if (!machine) {
    return (
      <div className="p-10 sm:p-14 bg-white border border-dashed border-slate-300 rounded-2xl text-center flex flex-col items-center justify-center">
        <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-3">
          <AlertTriangle className="w-7 h-7" />
        </div>
        <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
          Machine Not Found
        </h3>
        <p className="text-xs sm:text-sm text-slate-500 max-w-sm mb-5">
          The requested equipment does not exist or has been decommissioned.
        </p>
        <Link
          to="/machines"
          className="btn btn-secondary inline-flex text-xs sm:text-sm py-2 px-4 shadow-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Machines
        </Link>
      </div>
    );
  }

  const status = statusConfig[machine.status] || statusConfig.offline;
  const activeAlertsCount = Number(alertsData?.activeCount ?? machine.active_alerts) || 0;
  const alerts = alertsData?.data || [];

  const tabs = [
    { id: "overview", label: "Overview", icon: Cpu },
    {
      id: "sensors",
      label: "Sensors",
      icon: TrendingUp,
      count: machine.sensors?.length > 0 ? machine.sensors.length : undefined,
    },
    {
      id: "maintenance",
      label: "Maintenance",
      icon: Wrench,
      count:
        machine.recentMaintenance?.length > 0
          ? machine.recentMaintenance.length
          : undefined,
    },
    {
      id: "alerts",
      label: "Alerts",
      icon: AlertTriangle,
      badgeCount: activeAlertsCount,
    },
    {
      id: "sops",
      label: "SOPs",
      icon: FileText,
      count: machineSops.length > 0 ? machineSops.length : undefined,
    },
  ];

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3 sm:gap-3.5 min-w-0">
          <Link
            to="/machines"
            className="p-2 sm:p-2.5 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 border border-slate-200/90 rounded-xl shadow-xs transition-colors shrink-0"
            title="Back to fleet"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight truncate">
                {machine.name}
              </h1>
              <span
                className={clsx(
                  "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border shrink-0 gap-1.5",
                  status.badge
                )}
              >
                <span className={clsx("w-2 h-2 rounded-full", status.dot)} />
                {status.label}
              </span>
              {machine.code && (
                <span className="text-xs font-mono font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200/80 shrink-0">
                  {machine.code}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs sm:text-sm text-slate-500 mt-1">
              <span>{machine.model || "Standard Model"}</span>
              <span>•</span>
              <span>{machine.manufacturer || "OEM Equipment"}</span>
              {machine.location_in_plant && (
                <>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    {machine.location_in_plant}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Header Action Buttons */}
        <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap sm:flex-nowrap">
          <Link to={`/assistant?machineId=${id}`} className="flex-1 sm:flex-initial">
            <button
              type="button"
              className="btn btn-secondary w-full sm:w-auto text-xs sm:text-sm py-2 px-3.5 shadow-xs"
            >
              <MessageSquare className="w-4 h-4 text-slate-500" />
              <span>Ask AI Assistant</span>
            </button>
          </Link>

          <HasPermission allowedRoles={PERMISSIONS.MANAGE_MACHINES}>
            <button
              type="button"
              onClick={() => setIsConfigModalOpen(true)}
              className="btn btn-primary text-xs sm:text-sm py-2 px-3.5 shadow-md shadow-blue-500/20 whitespace-nowrap"
            >
              <Settings className="w-4 h-4" />
              <span>Configure</span>
            </button>
          </HasPermission>
        </div>
      </div>

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <StatCard
          title="Health Score"
          value={`${healthData?.healthScore !== undefined ? healthData.healthScore.toFixed(0) : "--"}%`}
          subtitle={
            healthData?.healthScore >= 80
              ? "Optimal"
              : healthData?.healthScore >= 60
                ? "Fair"
                : "Action Needed"
          }
          icon={Activity}
          color={
            healthData?.healthScore >= 80
              ? "emerald"
              : healthData?.healthScore >= 60
                ? "blue"
                : "rose"
          }
        />
        <StatCard
          title="Active Alerts"
          value={activeAlertsCount}
          subtitle={activeAlertsCount === 0 ? "Normal" : "Unresolved"}
          icon={AlertTriangle}
          color={activeAlertsCount === 0 ? "emerald" : "rose"}
        />
        <StatCard
          title="Last Maintenance"
          value={
            machine.last_maintenance_date
              ? formatIstDate(machine.last_maintenance_date)
              : "Never"
          }
          subtitle={machine.last_maintenance_date ? "Logged" : "None"}
          icon={Wrench}
          color="indigo"
        />
        <StatCard
          title="Next Maintenance"
          value={
            machine.next_maintenance_date
              ? formatIstDate(machine.next_maintenance_date)
              : "Not scheduled"
          }
          subtitle="Scheduled"
          icon={Clock}
          color="amber"
        />
      </div>

      {/* Tabs Pill Navigation */}
      <div>
        <div className="inline-flex items-center gap-1 p-1 bg-white rounded-full border border-slate-200/80 shadow-xs max-w-full overflow-x-auto no-scrollbar">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  "px-4 sm:px-5 py-2 text-xs sm:text-sm rounded-full transition-all duration-200 flex items-center gap-2 whitespace-nowrap shrink-0",
                  isActive
                    ? "bg-blue-600 text-white shadow-sm shadow-blue-500/25 font-bold"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 font-medium"
                )}
              >
                <Icon
                  className={clsx(
                    "w-4 h-4",
                    isActive ? "text-white" : "text-slate-500"
                  )}
                />
                <span>{tab.label}</span>
                {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
                  <span
                    className="px-1.5 py-0.5 min-w-[18px] text-[10px] font-bold rounded-full leading-none flex items-center justify-center bg-rose-500 text-white shadow-xs"
                  >
                    {tab.badgeCount}
                  </span>
                )}
                {tab.count !== undefined && tab.count > 0 && (
                  <span
                    className={clsx(
                      "text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none",
                      isActive
                        ? "bg-blue-500/80 text-white"
                        : "bg-slate-200 text-slate-600"
                    )}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={contentVariants}
        >
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 items-start">
              {/* Specifications Card */}
              <div className="bg-white border border-slate-200/80 p-5 sm:p-6 rounded-2xl shadow-sm h-fit">
                <div className="flex items-center gap-3 pb-4 mb-4 border-b border-slate-100">
                  <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center shrink-0">
                    <Settings className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-slate-900">
                      Specifications
                    </h3>
                    <p className="text-xs text-slate-500">
                      Hardware details & plant placement
                    </p>
                  </div>
                </div>

                <div className="divide-y divide-slate-100">
                  {[
                    {
                      label: "Serial Number",
                      value: machine.serial_number || "N/A",
                      icon: Hash,
                      mono: true,
                    },
                    {
                      label: "Model",
                      value: machine.model || "N/A",
                      icon: Tag,
                    },
                    {
                      label: "Manufacturer / OEM",
                      value: machine.manufacturer || "N/A",
                      icon: Factory,
                    },
                    {
                      label: "Plant Location",
                      value: machine.location_in_plant || "N/A",
                      icon: MapPin,
                    },
                    {
                      label: "Installation Date",
                      value: machine.installation_date
                        ? formatIstDate(machine.installation_date)
                        : "N/A",
                      icon: Calendar,
                    },
                    {
                      label: "Parent Facility",
                      value: machine.plant_name || "Plant Floor",
                      icon: Cpu,
                    },
                  ].map((item, index) => {
                    const ItemIcon = item.icon;
                    return (
                      <div
                        key={index}
                        className="py-3 flex items-center justify-between gap-3 first:pt-0 last:pb-0"
                      >
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <ItemIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span>{item.label}</span>
                        </div>
                        <p
                          className={clsx(
                            "text-xs sm:text-sm font-semibold text-slate-800 text-right truncate max-w-[55%]",
                            item.mono && "font-mono"
                          )}
                        >
                          {item.value}
                        </p>
                      </div>
                    );
                  })}
                </div>
                {(machine.specifications && Object.keys(machine.specifications).length > 0) && (
                  <div className="mt-5 pt-4 border-t border-slate-100">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Technical specifications</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {Object.entries(machine.specifications).map(([key, value]) => (
                        <div key={key} className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                          <p className="text-[11px] text-slate-400 capitalize">{key.replaceAll("_", " ")}</p>
                          <p className="text-xs font-semibold text-slate-800 mt-1 break-words">{typeof value === "object" ? JSON.stringify(value) : String(value)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sensor Trends Chart Card */}
              <div className="lg:col-span-2 bg-white border border-slate-200/80 p-5 sm:p-6 rounded-2xl shadow-sm h-fit">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 mb-4 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center shrink-0">
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-slate-900">
                        Sensor Trends
                      </h3>
                      <p className="text-xs text-slate-500">
                        24-hour telemetry operating performance
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                      <span className="text-slate-600 font-medium">Temperature (°C)</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                      <span className="text-slate-600 font-medium">Pressure (Bar)</span>
                    </div>
                  </div>
                </div>

                <div className="h-64 sm:h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={sensorHistory}
                      margin={{ top: 10, right: 10, bottom: 0, left: -20 }}
                    >
                      <defs>
                        <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="pressureGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis
                        dataKey="time"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#64748b", fontSize: 11 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#64748b", fontSize: 11 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "rgba(255, 255, 255, 0.96)",
                          backdropFilter: "blur(4px)",
                          border: "1px solid #e2e8f0",
                          borderRadius: "12px",
                          fontSize: "12px",
                          padding: "8px 12px",
                          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.08)",
                        }}
                        labelStyle={{ fontWeight: "bold", color: "#0f172a", marginBottom: "4px" }}
                      />
                      <Area
                        type="monotone"
                        dataKey="temp"
                        stroke="#3b82f6"
                        strokeWidth={2.5}
                        fill="url(#tempGradient)"
                        name="Temperature (°C)"
                      />
                      <Area
                        type="monotone"
                        dataKey="pressure"
                        stroke="#10b981"
                        strokeWidth={2.5}
                        fill="url(#pressureGradient)"
                        name="Pressure (Bar)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Telemetry Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-100">
                  <div className="bg-slate-50/80 rounded-xl p-3 border border-slate-100/80">
                    <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Avg Temp</p>
                    <p className="text-base sm:text-lg font-bold text-slate-800 mt-0.5">49.1°C</p>
                    <span className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Nominal
                    </span>
                  </div>
                  <div className="bg-slate-50/80 rounded-xl p-3 border border-slate-100/80">
                    <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Peak Temp</p>
                    <p className="text-base sm:text-lg font-bold text-slate-800 mt-0.5">55.0°C</p>
                    <span className="text-[11px] text-slate-500 font-medium mt-0.5">@ 12:00 PM</span>
                  </div>
                  <div className="bg-slate-50/80 rounded-xl p-3 border border-slate-100/80">
                    <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Avg Pressure</p>
                    <p className="text-base sm:text-lg font-bold text-slate-800 mt-0.5">5.4 Bar</p>
                    <span className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Stable
                    </span>
                  </div>
                  <div className="bg-slate-50/80 rounded-xl p-3 border border-slate-100/80">
                    <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Status</p>
                    <p className="text-base sm:text-lg font-bold text-emerald-600 mt-0.5">Optimal</p>
                    <span className="text-[11px] text-slate-500 font-medium mt-0.5">24h Continuous</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sensors Tab */}
          {activeTab === "sensors" && (
            <div className="space-y-4">
              {machine.sensors && machine.sensors.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5">
                    {machine.sensors
                      .slice(
                        (sensorsPage - 1) * SENSORS_PER_PAGE,
                        sensorsPage * SENSORS_PER_PAGE
                      )
                      .map((sensor) => (
                        <SensorCard
                          key={sensor.id}
                          sensor={sensor}
                          machineId={id}
                          onReadingAdded={handleReadingAdded}
                        />
                      ))}
                  </div>
                  <Pagination
                    currentPage={sensorsPage}
                    totalItems={machine.sensors.length}
                    pageSize={SENSORS_PER_PAGE}
                    onPageChange={setSensorsPage}
                    label="sensors"
                  />
                </>
              ) : (
                <div className="col-span-full p-10 sm:p-14 bg-white border border-dashed border-slate-300 rounded-2xl text-center flex flex-col items-center justify-center">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
                    <Activity className="w-7 h-7" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                    No Sensors Configured
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 max-w-sm mb-5">
                    No active telemetry sensors are attached to this machine unit.
                  </p>
                  <HasPermission allowedRoles={PERMISSIONS.MANAGE_MACHINES}>
                    <button
                      onClick={() => setIsConfigModalOpen(true)}
                      className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 inline-flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Configure Sensors
                    </button>
                  </HasPermission>
                </div>
              )}
            </div>
          )}

          {/* Maintenance Tab */}
          {activeTab === "maintenance" && (
            <div className="space-y-3 sm:space-y-3.5">
              {machine.recentMaintenance && machine.recentMaintenance.length > 0 ? (
                <>
                  {machine.recentMaintenance
                    .slice(
                      (maintenancePage - 1) * MAINTENANCE_PER_PAGE,
                      maintenancePage * MAINTENANCE_PER_PAGE
                    )
                    .map((log, index) => (
                      <motion.div
                        key={log.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.04 }}
                        className="bg-white border border-slate-200/80 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl shadow-xs hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center gap-3.5">
                          <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center shrink-0">
                            <Wrench className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-bold text-slate-900 text-sm sm:text-base">
                              {log.title}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {formatIstDate(log.created_at)} •{" "}
                              <span className="capitalize font-medium text-slate-700">
                                {log.maintenance_type}
                              </span>
                              {log.work_order_number && (
                                <span className="ml-2 font-semibold text-blue-600">
                                  • WO: {log.work_order_number}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <span
                          className={clsx(
                            "px-2.5 py-1 text-xs font-semibold rounded-full border capitalize self-start sm:self-auto",
                            log.status === "completed"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200/80"
                              : "bg-amber-50 text-amber-700 border-amber-200/80"
                          )}
                        >
                          {log.status}
                        </span>
                      </motion.div>
                    ))}
                  <Pagination
                    currentPage={maintenancePage}
                    totalItems={machine.recentMaintenance.length}
                    pageSize={MAINTENANCE_PER_PAGE}
                    onPageChange={setMaintenancePage}
                    label="maintenance logs"
                  />
                </>
              ) : (
                <div className="p-10 sm:p-14 bg-white border border-dashed border-slate-300 rounded-2xl text-center flex flex-col items-center justify-center">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
                    <ClipboardList className="w-7 h-7" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                    No Maintenance Logs
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 max-w-sm">
                    No previous service tasks or inspection logs have been registered for this unit.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Alerts Tab */}
          {activeTab === "alerts" && (
            <div className="space-y-3 sm:space-y-3.5">
              {alertsLoading ? (
                <div className="p-10 text-center text-sm text-slate-500">Loading alerts...</div>
              ) : alerts.length > 0 ? (
                <>
                  {alerts.map((alert, index) => (
                      <motion.div
                        key={alert.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.04 }}
                        className={clsx(
                          "bg-white p-4 sm:p-5 border rounded-2xl shadow-xs transition-shadow",
                          alert.severity === "critical"
                            ? "border-rose-200 bg-rose-50/30"
                            : alert.severity === "high"
                              ? "border-amber-200 bg-amber-50/30"
                              : "border-slate-200/80"
                        )}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div
                              className={clsx(
                                "w-9 h-9 rounded-xl flex items-center justify-center shrink-0",
                                alert.severity === "critical"
                                  ? "bg-rose-100 text-rose-600"
                                  : alert.severity === "high"
                                    ? "bg-amber-100 text-amber-600"
                                    : "bg-blue-100 text-blue-600"
                              )}
                            >
                              <AlertTriangle className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-bold text-slate-900 text-sm sm:text-base">
                                {alert.title}
                              </p>
                              <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">
                                {alert.message}
                              </p>
                              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatIstDateTime(alert.created_at)}
                              </p>
                            </div>
                          </div>
                          <span
                            className={clsx(
                              "px-2.5 py-0.5 text-xs font-semibold rounded-full border self-start sm:self-auto",
                              alert.is_resolved
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200/80"
                                : alert.linked_maintenance_id
                                  ? "bg-blue-50 text-blue-700 border-blue-200/80"
                                : "bg-rose-50 text-rose-700 border-rose-200/80"
                            )}
                          >
                            {alert.is_resolved
                              ? "Resolved"
                              : alert.linked_maintenance_id
                                ? "Maintenance linked"
                                : "Active Alert"}
                          </span>
                        </div>
                        {!alert.is_resolved &&
                          !alert.linked_maintenance_id &&
                          !createdMaintenanceAlertIds.has(alert.id) && (
                          <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
                            <div className="mt-4 pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <span className="text-xs text-slate-500">Create maintenance linked to this alert.</span>
                              <button
                                type="button"
                                onClick={() => createMaintenanceMutation.mutate(alert)}
                                disabled={createMaintenanceMutation.isPending}
                                className="btn btn-secondary text-xs py-2 px-3 inline-flex items-center gap-1.5"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                {createMaintenanceMutation.isPending ? "Creating..." : "Create maintenance"}
                              </button>
                            </div>
                          </HasPermission>
                        )}
                        {(alert.linked_maintenance_id || createdMaintenanceAlertIds.has(alert.id)) && (
                          <p className="mt-4 pt-3 border-t border-slate-100 text-xs font-semibold text-emerald-600">
                            Maintenance created for this alert.
                          </p>
                        )}
                      </motion.div>
                    ))}
                  <Pagination
                    currentPage={alertsPage}
                    totalItems={alertsData?.pagination?.total || 0}
                    pageSize={ALERTS_PER_PAGE}
                    onPageChange={setAlertsPage}
                    label="alerts"
                  />
                </>
              ) : (
                <div className="p-10 sm:p-14 bg-white border border-dashed border-slate-300 rounded-2xl text-center flex flex-col items-center justify-center">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
                    <CheckCircle className="w-7 h-7" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                    All Clear
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 max-w-sm">
                    No active or historical telemetry alerts detected for this equipment.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* SOPs Tab */}
          {activeTab === "sops" && (
            <div className="space-y-3 sm:space-y-3.5">
              {machineSops && machineSops.length > 0 ? (
                <>
                  <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs overflow-hidden divide-y divide-slate-100">
                    {machineSops
                      .slice(
                        (sopsPage - 1) * SOPS_PER_PAGE,
                        sopsPage * SOPS_PER_PAGE
                      )
                      .map((sop, index) => {
                        const sopStatus =
                          statusConfig[sop.status] || {
                            badge: "bg-slate-100 text-slate-700 border-slate-200",
                            dot: "bg-slate-400",
                            label: sop.status || "Draft",
                          };

                        return (
                          <motion.div
                            key={sop.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-50/70 transition-colors group"
                          >
                            <div className="flex items-start sm:items-center gap-3.5 min-w-0 flex-1">
                              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-200">
                                <FileText className="w-5 h-5" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Link
                                    to={`/sops/${sop.id}`}
                                    className="font-bold text-slate-900 hover:text-blue-600 transition-colors text-sm sm:text-base truncate"
                                  >
                                    {sop.title}
                                  </Link>
                                  {sop.is_ai_generated && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 shrink-0">
                                      AI
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-1">
                                  <span className="font-medium text-slate-600">
                                    {sop.category || "General"}
                                  </span>
                                  {sop.estimated_duration && (
                                    <>
                                      <span className="text-slate-300">•</span>
                                      <span className="flex items-center gap-1">
                                        <Clock className="w-3 h-3 text-slate-400" />
                                        {sop.estimated_duration} mins
                                      </span>
                                    </>
                                  )}
                                  {sop.steps?.length > 0 && (
                                    <>
                                      <span className="text-slate-300">•</span>
                                      <span>{sop.steps.length} steps</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-2.5 self-start sm:self-auto shrink-0">
                              <span
                                className={clsx(
                                  "px-2.5 py-0.5 text-xs font-semibold rounded-full border capitalize inline-flex items-center gap-1.5",
                                  sopStatus.badge
                                )}
                              >
                                <span className={clsx("w-1.5 h-1.5 rounded-full", sopStatus.dot)} />
                                {sopStatus.label}
                              </span>
                              <Link
                                to={`/sops/${sop.id}`}
                                className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Open procedure"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </Link>
                            </div>
                          </motion.div>
                        );
                      })}
                  </div>
                  <Pagination
                    currentPage={sopsPage}
                    totalItems={machineSops.length}
                    pageSize={SOPS_PER_PAGE}
                    onPageChange={setSopsPage}
                    label="procedures"
                  />
                </>
              ) : (
                <div className="p-10 sm:p-14 bg-white border border-dashed border-slate-300 rounded-2xl text-center flex flex-col items-center justify-center">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
                    <BookOpen className="w-7 h-7" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                    No Standard Operating Procedures
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 mb-6 max-w-md">
                    No specific procedures have been attached directly to this unit. You can view all procedures across the facility in the SOPs directory.
                  </p>
                  <Link
                    to={`/sops`}
                    className="btn btn-primary inline-flex items-center gap-2 text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20"
                  >
                    <FileText className="w-4 h-4" />
                    <span>View Facility SOPs</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <MachineConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        machine={machine}
      />

      {/* Sensor Threshold Breach Alert Modal - Styled matching SOPs.jsx portal pattern */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {readingAlertModal && (
              <div
                className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
                onClick={() => setReadingAlertModal(null)}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="relative w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 space-y-4 my-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 border border-rose-100/80 flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                        Threshold Alert Triggered
                      </h2>
                      <p className="text-xs text-rose-600 font-medium">
                        Operating boundary breach detected
                      </p>
                    </div>
                  </div>

                  {/* Warning Description */}
                  <div className="text-xs sm:text-sm text-slate-600 leading-relaxed space-y-2">
                    <p>
                      Sensor <span className="font-semibold text-slate-900">{readingAlertModal.sensor?.sensor_name}</span> reported a value of{" "}
                      <span className="font-mono font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">
                        {readingAlertModal.value?.toFixed?.(1) || readingAlertModal.value} {readingAlertModal.sensor?.unit}
                      </span>
                      .
                    </p>
                    <p className="text-xs text-slate-500">
                      This reading breaches the configured{" "}
                      <span className="font-semibold text-slate-700">
                        {readingAlertModal.breachType === "low"
                          ? `minimum threshold (${readingAlertModal.min} ${readingAlertModal.sensor?.unit})`
                          : `maximum threshold (${readingAlertModal.max} ${readingAlertModal.sensor?.unit})`}
                      </span>
                      . Equipment health score and telemetry alerts have been registered.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setReadingAlertModal(null)}
                      className="btn btn-secondary text-xs sm:text-sm py-2 px-4 hover:border-slate-300"
                    >
                      Dismiss
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setReadingAlertModal(null);
                        setActiveTab("alerts");
                      }}
                      className="btn bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm py-2 px-4 shadow-md shadow-rose-500/20 inline-flex items-center gap-2 font-medium"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      <span>View in Alerts</span>
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default MachineDetail;
